import { Check, LoaderCircle } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { WatchRequest } from "../../chat/AskContext";
import type { ChatEntry } from "../../chat/chatStore";
import { hasProcessSelection } from "../../watchlist/types";
import { editableScope, pageDateFilters } from "../../chat/askScope";
import { useWatchContext, useWatchSetup } from "../../watchlist/useWatchFlow";
import WatchSetupWizard from "../watchlist/WatchSetupWizard";
import WatchlistComposer from "../watchlist/WatchlistComposer";

/** Mounted once per setup request: navigation cannot mutate an alert being configured. */
export function AskWatchSetup({
  request,
  entries,
  onClose,
  onCreated,
}: {
  request: WatchRequest;
  entries?: ChatEntry[];
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const context = useWatchContext({ defaultWindowDays: 30 });
  const watch = useWatchSetup(context);
  const [text, setText] = useState(request.prompt ?? "");
  const [savedTitle, setSavedTitle] = useState<string | null>(null);
  useEffect(() => {
    if (watch.saved) setSavedTitle(watch.saved);
  }, [watch.saved]);
  const reported = useRef(false);
  useEffect(() => {
    if (watch.savedAlertId && !reported.current) {
      reported.current = true;
      onCreated?.(watch.savedAlertId);
    }
  }, [watch.savedAlertId, onCreated]);
  const initialized = useRef(false);
  const [ready, setReady] = useState(false);
  const reviewStarted = useRef(false);
  const {
    scope,
    schedule,
    changeScope,
    changeSchedule,
    userOptions,
    processOptions,
    processOptionsLoaded,
  } = context;
  useEffect(() => {
    if (initialized.current || !processOptionsLoaded || !context.userOptionsLoaded) return;
    initialized.current = true;
    const selectionDates = request.context?.runSelection;
    const sourceFilters = selectionDates
      ? { ...request.context?.filters, ...pageDateFilters(selectionDates.from, selectionDates.to) }
      : request.context?.filters;
    const scope =
      request.scope ??
      request.context?.customScope ??
      editableScope(sourceFilters, userOptions, processOptions);
    context.setScope(
      request.processSlug
        ? { ...scope, processSlugs: [request.processSlug], allProcesses: false }
        : scope,
    );
    context.setScopeTouched({ people: true, processes: true, window: true, schedule: false });
    watch.beginDirectSetup();
    setReady(true);
  }, [processOptionsLoaded, userOptions, processOptions, request, context, watch]);
  useEffect(() => {
    if (
      ready &&
      request.fromConversation &&
      !reviewStarted.current &&
      scope.userIds.length &&
      hasProcessSelection(scope) &&
      request.prompt?.trim()
    ) {
      reviewStarted.current = true;
      watch.reviewWatch(request.prompt.trim(), entries ?? [], [], "conversation", 1);
    }
  }, [ready, request, scope.userIds.length, entries, watch]);
  const { setup } = watch;
  return (
    <div
      data-tour="watch-setup-wizard"
      className="flex h-full min-w-0 flex-col overflow-y-auto p-4"
    >
      {!savedTitle && (
        <div className="mb-4 flex items-start justify-between gap-3">
          <p className="text-xs text-text-dim">{t("chat_kpi_scope_help")}</p>
          <button
            type="button"
            disabled={watch.saving}
            className="shrink-0 text-sm text-primary disabled:opacity-40"
            onClick={() => {
              watch.cancelSetup();
              onClose();
            }}
          >
            {t("cancel")}
          </button>
        </div>
      )}
      {!savedTitle &&
        Boolean(
          request.context?.filters.instanceIds || request.context?.filters.selectedSteps?.length,
        ) && <p className="mb-4 text-xs text-text-mid">{t("chat_kpi_process_help")}</p>}
      {!savedTitle && request.context?.filters.recordingIds && (
        <p className="mb-4 text-xs text-text-mid">{t("chat_kpi_session_help")}</p>
      )}
      {savedTitle ? (
        <div
          data-tour="wizard-saved"
          className="mx-auto mt-4 w-full max-w-lg rounded-2xl border border-outline bg-surface p-5 sm:p-6"
        >
          <div role="status">
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-success/10 text-success-strong">
              <Check size={24} strokeWidth={2.5} aria-hidden="true" />
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight text-text">
              {t("chat_kpi_saved")}
            </h3>
            <p className="mt-2 break-words text-base font-medium leading-6 text-text">
              {savedTitle}
            </p>
            <p className="mt-2 text-sm leading-6 text-text-mid">{t("chat_kpi_saved_help")}</p>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex min-h-10 items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
              onClick={() => {
                onClose();
                navigate(`/watchlist/${watch.savedAlertId}/`);
              }}
            >
              {t("chat_kpi_view")}
            </button>
            <button
              type="button"
              className="inline-flex min-h-10 items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-text-mid transition hover:bg-muted-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              onClick={onClose}
            >
              {t(request.fromConversation ? "chat_back_to_conversation" : "close")}
            </button>
          </div>
        </div>
      ) : watch.saving || (setup.review && setup.questionIndex === null) ? (
        <div role="status" className="flex items-center gap-2 text-sm text-text-mid">
          {watch.saving ? (
            <>
              <LoaderCircle size={16} className="animate-spin" aria-hidden="true" />
              {t("chat_kpi_creating")}
            </>
          ) : (
            <button
              type="button"
              className="text-primary"
              onClick={() =>
                watch.watchPrompt(setup.review!.prompt.trim(), setup.review!.title.trim())
              }
            >
              {t("chat_kpi_retry")}
            </button>
          )}
        </div>
      ) : setup.review && setup.questionIndex !== null ? (
        <WatchSetupWizard
          review={setup.review}
          round={setup.round}
          questionIndex={setup.questionIndex}
          answers={setup.answers}
          onAnswer={(questionId, answer) =>
            watch.dispatchSetup({ type: "set_answer", questionId, answer })
          }
          onNext={watch.nextSetupQuestion}
          onBack={watch.backInSetup}
          busy={setup.reviewing || watch.saving}
        />
      ) : (
        <div data-tour="watchlist-ask">
          {!request.fromConversation && (
            <div className="mb-4 space-y-3">
              <h3 className="text-base font-semibold text-text">{t("askPanel.kpi_question")}</h3>
              <p className="text-sm text-text-mid">{t("askPanel.kpi_custom")}</p>
              {(
                [
                  ["metric_duration", "chat_alert_example_duration"],
                  ["metric_rework", "chat_alert_example_rework"],
                  ["metric_manual", "chat_alert_example_manual"],
                ] as const
              ).map(([label, key]) => (
                <button
                  type="button"
                  key={key}
                  disabled={setup.reviewing}
                  onClick={() => setText(t(key))}
                  aria-pressed={text === t(key)}
                  className={`block w-full rounded-xl border px-3 py-3 text-left text-sm hover:bg-muted-surface ${text === t(key) ? "border-info bg-info/5" : "border-outline"}`}
                >
                  <span className="block font-medium text-text">{t(`askPanel.${label}`)}</span>
                  <span className="mt-1 block text-xs text-text-mid">{t(key)}</span>
                </button>
              ))}
            </div>
          )}
          <WatchlistComposer
            fixedHeight
            settingUpWatch
            text={text}
            onTextChange={setText}
            scope={scope}
            onScopeChange={changeScope}
            schedule={schedule}
            onScheduleChange={changeSchedule}
            userOptions={userOptions}
            processOptions={processOptions}
            onSubmit={() => {
              if (text.trim() && scope.userIds.length)
                watch.reviewWatch(
                  text.trim(),
                  entries ??
                    (request.context
                      ? [
                          {
                            kind: "message",
                            role: "user",
                            content: t("askPanel.kpi_source_prompt", {
                              source:
                                [
                                  request.context.label,
                                  request.context.filters.instanceIds?.join(", "),
                                ]
                                  .filter(Boolean)
                                  .join(" (") + (request.context.filters.instanceIds ? ")" : ""),
                            }),
                          },
                        ]
                      : []),
                  [],
                  entries?.length ? "conversation" : "direct",
                  1,
                );
            }}
            busy={false}
            onStop={() => undefined}
            reviewing={setup.reviewing || watch.saving || !initialized.current}
            placeholder={t("watchlist.setup_placeholder")}
          />
        </div>
      )}
      {watch.watchSaveError && (
        <p role="alert" className="mt-3 text-sm text-danger-strong">
          {watch.watchSaveError}
        </p>
      )}
    </div>
  );
}
